import { fetchApi } from "./api";
import { API_ENDPOINTS } from "../config/api";
import type { Product } from "@/types/products";

/** Get product by ID. Used to resolve product page URL for scraping. */
export async function getProduct(productId: number): Promise<Product> {
  return fetchApi<Product>(API_ENDPOINTS.product(productId));
}

/** Resolve the product page URL for a given product ID (product and variants share the same page). */
export async function getProductUrl(productId: number): Promise<string | null> {
  const product = await getProduct(productId);
  return product.details?.url ?? product.purchase_options?.[0]?.url ?? null;
}

/** Get product by variant ID (GET /api/products/by-variant/{variant_id}). Returns full product payload. */
export async function getProductByVariant(variantId: number): Promise<Product> {
  return fetchApi<Product>(API_ENDPOINTS.productByVariant(variantId));
}

export interface SearchProductsByNameOptions {
  source?: string;
  available_only?: boolean;
  limit?: number;
}

/** Search products by name (GET /api/products/search/name?query=&source=&available_only=&limit=). */
export async function searchProductsByName(
  query: string,
  opts: SearchProductsByNameOptions = {},
): Promise<Product[]> {
  const params = new URLSearchParams({ query });
  if (opts.source) params.set("source", opts.source);
  if (opts.available_only) params.set("available_only", "true");
  if (opts.limit != null) params.set("limit", String(opts.limit));
  return fetchApi<Product[]>(`${API_ENDPOINTS.productsSearchName}?${params}`);
}

export interface SearchProductsByUrlOptions {
  limit?: number;
}

/** Search products by URL (GET /api/products/search/url?query=&limit=). */
export async function searchProductsByUrl(
  query: string,
  opts: SearchProductsByUrlOptions = {},
): Promise<Product[]> {
  const params = new URLSearchParams({ query });
  if (opts.limit != null) params.set("limit", String(opts.limit));
  return fetchApi<Product[]>(`${API_ENDPOINTS.productsSearchUrl}?${params}`);
}
